import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { anomalyApi } from '../services/api';
import { formatBSDateTime, formatBSDateTimeShort } from '../utils/bs';
import { NepaliDateTimePicker } from '../components/NepaliDatePicker';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import type { PlantHealthSummary, TagHealthScore, EarlyWarning, AnomalyType, LoggedAnomaly, AlarmSeverity } from '../types';

// ── Visual components ─────────────────────────────────────────────────────────

function HealthRing({ score, size = 120 }: { score: number; size?: number }) {
  const r = size * 0.38;
  const cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const filled = (score / 100) * circumference;
  const color = score >= 85 ? '#16a34a' : score >= 60 ? '#d97706' : '#dc2626';
  const textColor = score >= 85 ? '#15803d' : score >= 60 ? '#b45309' : '#b91c1c';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={size*0.12} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={size*0.12}
        strokeDasharray={`${filled} ${circumference-filled}`}
        strokeDashoffset={circumference*0.25} strokeLinecap="round"
        style={{ transition:'stroke-dasharray 0.8s ease' }} />
      <text x={cx} y={cy-4} textAnchor="middle" fontSize={size*0.22}
        fontWeight="800" fill={textColor} fontFamily="monospace" dominantBaseline="middle">{score}</text>
      <text x={cx} y={cy+size*0.2} textAnchor="middle" fontSize={size*0.1} fill="#94a3b8">/ 100</text>
    </svg>
  );
}

function StatusBadge({ status }: { status: 'healthy' | 'warning' | 'critical' }) {
  const cls = status === 'healthy' ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
    : status === 'warning' ? 'bg-amber-100 text-amber-700 border-amber-300'
    : 'bg-red-100 text-red-700 border-red-300';
  return <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${cls}`}>{status.toUpperCase()}</span>;
}

function TrendArrow({ trend }: { trend: 'stable' | 'rising' | 'falling' }) {
  if (trend === 'rising')  return <span className="text-red-600 text-xs">↑ Rising</span>;
  if (trend === 'falling') return <span className="text-blue-600 text-xs">↓ Falling</span>;
  return <span className="text-slate-400 text-xs">→ Stable</span>;
}

const SEVERITY_CLASS: Record<AlarmSeverity, string> = {
  critical: 'badge-crit', warning: 'badge-warn', info: 'badge-info',
};
const STATUS_CLS: Record<string, string> = {
  active: 'badge-crit', acknowledged: 'badge-warn', resolved: 'badge-ok',
};
const CAT_ICON: Record<string, string> = {
  process:'⚡', sensor:'📡', hydraulic:'💧', mechanical:'⚙️', electrical:'🔌', environmental:'🌿',
};

// ── Log Anomaly modal ─────────────────────────────────────────────────────────

function LogAnomalyModal({ anomalyTypes, onClose }: { anomalyTypes: AnomalyType[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    anomaly_type_id: '',
    severity:        '',
    message:         '',
    reported_at:     new Date().toISOString().slice(0,16),
    notes:           '',
  });
  const selectedType = anomalyTypes.find(t => t.id === Number(form.anomaly_type_id));

  const mut = useMutation({
    mutationFn: () => anomalyApi.createLogged({
      anomaly_type_id: Number(form.anomaly_type_id),
      severity:        form.severity  || undefined,
      message:         form.message   || undefined,
      reported_at:     form.reported_at ? new Date(form.reported_at).toISOString() : undefined,
      notes:           form.notes     || undefined,
    }),
    onSuccess: () => {
      toast.success('Anomaly logged');
      qc.invalidateQueries({ queryKey: ['logged-anomalies'] });
      onClose();
    },
    onError: () => toast.error('Failed to log anomaly'),
  });

  const byCategory: Record<string, AnomalyType[]> = {};
  anomalyTypes.forEach(t => { (byCategory[t.category] ??= []).push(t); });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border">
          <h2 className="text-base font-bold text-slate-900">Log Anomaly</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Anomaly Type <span className="text-red-500">*</span></label>
            <select className="input w-full" value={form.anomaly_type_id}
              onChange={e => {
                const t = anomalyTypes.find(a => a.id === Number(e.target.value));
                setForm(p => ({ ...p, anomaly_type_id: e.target.value, severity: t?.default_severity ?? '', message: t?.name ?? '' }));
              }}
            >
              <option value="">— Select anomaly type —</option>
              {Object.entries(byCategory).map(([cat, types]) => (
                <optgroup key={cat} label={`${CAT_ICON[cat] ?? '•'} ${cat.charAt(0).toUpperCase()+cat.slice(1)}`}>
                  {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </optgroup>
              ))}
            </select>
            {selectedType?.description && <p className="text-xs text-slate-400 mt-1">{selectedType.description}</p>}
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">When Observed <span className="text-red-500">*</span></label>
            <NepaliDateTimePicker
              value={form.reported_at}
              onChange={v => setForm(p => ({ ...p, reported_at: v }))}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Severity</label>
            <select className="input w-full" value={form.severity} onChange={e => setForm(p => ({...p, severity: e.target.value}))}>
              <option value="">— Use default —</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Description</label>
            <input className="input w-full" value={form.message}
              onChange={e => setForm(p => ({...p, message: e.target.value}))}
              placeholder="Describe the observed anomaly…" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Notes</label>
            <textarea className="input w-full h-20 resize-none" value={form.notes}
              onChange={e => setForm(p => ({...p, notes: e.target.value}))}
              placeholder="Tag readings, conditions, possible causes…" />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button onClick={() => mut.mutate()} disabled={mut.isPending || !form.anomaly_type_id} className="btn-primary flex-1">
            {mut.isPending ? 'Logging…' : 'Log Anomaly'}
          </button>
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Acknowledge modal ─────────────────────────────────────────────────────────

function AckModal({ anomaly, onClose }: { anomaly: LoggedAnomaly; onClose: () => void }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');
  const mut = useMutation({
    mutationFn: () => anomalyApi.acknowledgeLogged(anomaly.id, notes || undefined),
    onSuccess: () => {
      toast.success('Acknowledged');
      qc.invalidateQueries({ queryKey: ['logged-anomalies'] });
      onClose();
    },
    onError: () => toast.error('Failed'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border">
          <h2 className="text-base font-bold text-slate-900">Acknowledge Anomaly</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="rounded-lg bg-slate-50 border border-hydro-border px-4 py-3">
            <p className="text-sm font-semibold text-slate-900">{anomaly.anomaly_type}</p>
            <p className="text-xs text-slate-500 mt-0.5">{anomaly.message}</p>
            <p className="text-xs text-slate-400 mt-1">{formatBSDateTime(new Date(anomaly.reported_at))}</p>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Acknowledgment Notes</label>
            <textarea autoFocus className="input w-full h-24 resize-none" value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Actions taken, investigation findings, remediation steps…" />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button onClick={() => mut.mutate()} disabled={mut.isPending} className="btn-primary flex-1">
            {mut.isPending ? 'Acknowledging…' : 'Acknowledge'}
          </button>
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Logged anomaly row ────────────────────────────────────────────────────────

function LoggedAnomalyRow({ a, canAck, onAck }: { a: LoggedAnomaly; canAck: boolean; onAck: (a: LoggedAnomaly) => void }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr className="table-row text-sm cursor-pointer hover:bg-slate-50/70" onClick={() => setExpanded(e => !e)}>
        <td className="px-4 py-3 text-xs font-mono text-slate-500 whitespace-nowrap">
          {formatBSDateTimeShort(new Date(a.reported_at))}
        </td>
        <td className="px-4 py-3">
          <p className="text-xs font-semibold text-slate-800">{a.anomaly_type}</p>
          {a.anomaly_category && <p className="text-xs text-slate-400">{CAT_ICON[a.anomaly_category]} {a.anomaly_category}</p>}
        </td>
        <td className="px-4 py-3"><span className={SEVERITY_CLASS[a.severity] ?? 'badge-info'}>{a.severity}</span></td>
        <td className="px-4 py-3"><span className={STATUS_CLS[a.status] ?? 'badge-info'}>{a.status}</span></td>
        <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">{a.message}</td>
        <td className="px-4 py-3 text-xs text-slate-400">{a.reported_by_name ?? '—'}</td>
        {canAck && (
          <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
            {a.status === 'active' && (
              <button onClick={() => onAck(a)} className="text-xs px-2 py-1 rounded bg-brand-700 hover:bg-brand-600 text-white">ACK</button>
            )}
          </td>
        )}
      </tr>
      {expanded && (
        <tr className="bg-slate-50/60">
          <td colSpan={canAck ? 7 : 6} className="px-6 py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div><p className="text-slate-400 mb-0.5">Reported by</p><p className="text-slate-700">{a.reported_by_name ?? '—'}</p></div>
              {a.acknowledged_at && (
                <div>
                  <p className="text-slate-400 mb-0.5">Acknowledged</p>
                  <p className="text-slate-700">{formatBSDateTime(new Date(a.acknowledged_at))}{a.acknowledged_by_name && <> · {a.acknowledged_by_name}</>}</p>
                </div>
              )}
              {a.acknowledge_notes && (
                <div className="col-span-2">
                  <p className="text-slate-400 mb-0.5">Ack Notes</p>
                  <p className="text-slate-700">{a.acknowledge_notes}</p>
                </div>
              )}
              {a.notes && (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-slate-400 mb-0.5">Notes</p>
                  <p className="text-slate-700">{a.notes}</p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Existing anomaly engine components ────────────────────────────────────────

function TagRow({ tag }: { tag: TagHealthScore }) {
  const barColor = tag.health_score >= 85 ? 'bg-emerald-500' : tag.health_score >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <tr className="border-b border-hydro-border hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3"><div><p className="text-sm text-slate-900 font-medium">{tag.display_name}</p><p className="text-xs text-slate-400 font-mono">{tag.tag_name}</p></div></td>
      <td className="px-4 py-3"><StatusBadge status={tag.status} /></td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width:`${tag.health_score}%` }} />
          </div>
          <span className="text-sm font-mono text-slate-900">{tag.health_score}%</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm font-mono text-slate-900">{tag.last_value !== null ? `${tag.last_value.toFixed(3)} ${tag.unit ?? ''}` : '—'}</td>
      <td className="px-4 py-3 text-sm font-mono text-slate-500">{tag.baseline_mean !== null ? tag.baseline_mean.toFixed(3) : '—'}</td>
      <td className="px-4 py-3 text-sm font-mono text-slate-500">
        {tag.sigma_deviation !== null
          ? <span className={Math.abs(tag.sigma_deviation) > 2 ? 'text-red-600' : 'text-slate-500'}>
              {tag.sigma_deviation > 0 ? '+' : ''}{tag.sigma_deviation}σ
            </span>
          : '—'}
      </td>
      <td className="px-4 py-3 text-sm font-mono">
        {tag.anomaly_count_24h > 0
          ? <span className="text-amber-600">{tag.anomaly_count_24h} / {tag.total_24h_readings}</span>
          : <span className="text-slate-400">{tag.anomaly_count_24h} / {tag.total_24h_readings}</span>}
      </td>
      <td className="px-4 py-3"><TrendArrow trend={tag.trend} /></td>
    </tr>
  );
}

function WarningCard({ warning }: { warning: EarlyWarning }) {
  const typeLabel: Record<string, string> = {
    APPROACHING_ALARM:'Threshold Alert', ANOMALOUS_BEHAVIOR:'Anomaly Detected',
    NO_DATA:'Data Loss', MULTI_SENSOR_CORRELATION:'Correlation', TREND_ALERT:'Trend Alert',
  };
  const cls = warning.severity === 'critical' ? 'border-red-400/60 bg-red-50'
    : warning.severity === 'warning' ? 'border-amber-400/60 bg-amber-50'
    : 'border-slate-200 bg-slate-50';
  const badgeCls = warning.severity === 'critical' ? 'bg-red-100 text-red-700'
    : warning.severity === 'warning' ? 'bg-amber-100 text-amber-700'
    : 'bg-slate-100 text-slate-600';
  return (
    <div className={`p-4 rounded-lg border ${cls}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${badgeCls}`}>{typeLabel[warning.warning_type] ?? warning.warning_type}</span>
          <span className="text-xs text-slate-500 font-mono">{warning.tag_name}</span>
        </div>
        <span className="text-xs text-slate-400 whitespace-nowrap">{formatBSDateTimeShort(new Date(warning.detected_at))}</span>
      </div>
      <p className={`text-sm ${warning.severity === 'critical' ? 'text-red-700' : warning.severity === 'warning' ? 'text-amber-700' : 'text-slate-600'}`}>{warning.message}</p>
      {(warning.current_value !== undefined || warning.baseline_mean !== undefined) && (
        <div className="mt-2 flex gap-4 text-xs text-slate-500 font-mono">
          {warning.current_value !== undefined && <span>Current: <span className="text-slate-700">{warning.current_value.toFixed(3)}</span></span>}
          {warning.baseline_mean !== undefined && <span>Baseline: <span className="text-slate-700">{warning.baseline_mean.toFixed(3)}</span></span>}
        </div>
      )}
    </div>
  );
}

// ── Main Anomaly page ─────────────────────────────────────────────────────────

export default function Anomaly() {
  const { user, hasPermission } = useAuthStore();
  const canLog = hasPermission('anomaly') || hasPermission('alarms');
  const plantId = user?.plant_id ?? 1;

  const [view,      setView]      = useState<'engine' | 'logged'>('engine');
  const [filter,    setFilter]    = useState<'all' | 'critical' | 'warning' | 'info'>('all');
  const [tagFilter, setTagFilter] = useState<'all' | 'healthy' | 'warning' | 'critical'>('all');
  const [logOpen,   setLogOpen]   = useState(false);
  const [ackItem,   setAckItem]   = useState<LoggedAnomaly | null>(null);
  const [logFilter, setLogFilter] = useState({ status: '', severity: '' });

  const { data, isLoading, dataUpdatedAt, refetch } = useQuery<PlantHealthSummary>({
    queryKey: ['plant-health-full', plantId],
    queryFn: () => anomalyApi.health(plantId, false).then(r => r.data.data),
    refetchInterval: 60_000, staleTime: 55_000,
  });

  const { data: typesData } = useQuery({
    queryKey: ['anomaly-types'],
    queryFn: () => anomalyApi.listTypes().then(r => r.data),
  });

  const { data: loggedData } = useQuery({
    queryKey: ['logged-anomalies', logFilter],
    queryFn: () => anomalyApi.listLogged({ ...logFilter, limit: 50 }).then(r => r.data),
    enabled: view === 'logged',
  });

  const anomalyTypes = typesData?.anomaly_types ?? [];
  const loggedList   = loggedData?.data ?? [];
  const loggedTotal  = loggedData?.total ?? 0;

  const filteredWarnings = data?.early_warnings.filter(w => filter === 'all' || w.severity === filter) ?? [];
  const filteredTags     = data?.tag_scores.filter(t => tagFilter === 'all' || t.status === tagFilter) ?? [];
  const lastUpdated = dataUpdatedAt ? formatBSDateTimeShort(new Date(dataUpdatedAt)) : '—';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Anomaly Detection</h1>
          <p className="text-xs text-slate-400 mt-0.5">Automated engine · Manual reporting · Acknowledgment tracking</p>
        </div>
        <div className="flex items-center gap-3">
          {canLog && <button onClick={() => setLogOpen(true)} className="btn-primary text-sm">+ Log Anomaly</button>}
          <span className="text-xs text-slate-400">Updated: {lastUpdated}</span>
          <button onClick={() => refetch()} className="btn-ghost text-xs px-3 py-1">Refresh</button>
        </div>
      </div>

      {/* View toggle */}
      <div className="flex gap-1 border border-hydro-border rounded-lg overflow-hidden w-fit">
        {(['engine','logged'] as const).map(v => (
          <button key={v} onClick={() => setView(v)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
              view === v ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {v === 'engine' ? '🧠 Detection Engine' : '📋 Logged Anomalies'}
            {v === 'logged' && loggedTotal > 0 && <span className="ml-1.5 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">{loggedTotal}</span>}
          </button>
        ))}
      </div>

      {/* ── Logged anomalies view ── */}
      {view === 'logged' && (
        <div className="space-y-4">
          <div className="card flex flex-wrap gap-4 items-center">
            <select value={logFilter.status} onChange={e => setLogFilter(f => ({...f, status: e.target.value}))} className="input w-40">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
            </select>
            <select value={logFilter.severity} onChange={e => setLogFilter(f => ({...f, severity: e.target.value}))} className="input w-40">
              <option value="">All Severity</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
            <button onClick={() => setLogFilter({ status:'', severity:'' })} className="btn-ghost text-sm">Clear</button>
            <span className="ml-auto text-sm text-slate-500">{loggedTotal} records</span>
          </div>

          <div className="card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider">
                    <th className="px-4 py-3 text-left">Time</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Severity</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Description</th>
                    <th className="px-4 py-3 text-left">Reported By</th>
                    {canLog && <th className="px-4 py-3 text-left">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {loggedList.length === 0
                    ? <tr><td colSpan={canLog ? 7 : 6} className="px-4 py-8 text-center text-slate-400">No logged anomalies</td></tr>
                    : loggedList.map(a => <LoggedAnomalyRow key={a.id} a={a} canAck={canLog} onAck={setAckItem} />)
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Detection engine view ── */}
      {view === 'engine' && (
        <>
          {isLoading && <div className="card text-center py-12 text-slate-400">Computing health scores from 7-day baseline…</div>}

          {data && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card flex flex-col items-center justify-center py-4">
                  <HealthRing score={data.overall_health} size={120} />
                  <p className="text-sm text-slate-500 mt-2 font-medium">Overall Plant Health</p>
                  <p className="text-xs text-slate-400 mt-1">Based on {data.total_tags} monitored tags</p>
                </div>
                <div className="card space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700">Tag Health Breakdown</h3>
                  {[
                    { label:'Healthy',  count: data.healthy_tags,  color:'bg-emerald-500', textColor:'text-emerald-700' },
                    { label:'Warning',  count: data.warning_tags,  color:'bg-amber-500',   textColor:'text-amber-600'   },
                    { label:'Critical', count: data.critical_tags, color:'bg-red-500',     textColor:'text-red-600'     },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-28 flex-shrink-0"><p className="text-xs text-slate-500">{item.label}</p></div>
                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${item.color}`} style={{ width:`${data.total_tags>0?(item.count/data.total_tags)*100:0}%` }} />
                      </div>
                      <span className={`text-sm font-mono font-bold ${item.textColor} w-8 text-right`}>{item.count}</span>
                    </div>
                  ))}
                </div>
                <div className="card">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Early Warning Summary</h3>
                  {data.early_warnings.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-20 text-emerald-600">
                      <span className="text-2xl">✓</span>
                      <p className="text-sm mt-1">No anomalies detected</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(['critical','warning','info'] as const).map(sev => {
                        const count = data.early_warnings.filter(w => w.severity === sev).length;
                        if (!count) return null;
                        const cls = sev==='critical' ? 'bg-red-100 text-red-600 border-red-300/60'
                          : sev==='warning' ? 'bg-amber-100 text-amber-600 border-amber-300/60'
                          : 'bg-slate-100 text-slate-600 border-slate-300/60';
                        return (
                          <div key={sev} className={`flex justify-between items-center p-2 rounded border ${cls}`}>
                            <span className="text-xs font-semibold uppercase">{sev}</span>
                            <span className="text-sm font-mono font-bold">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {data.early_warnings.length > 0 && (
                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-semibold text-slate-900">Early Warnings</h2>
                    <div className="flex gap-1">
                      {(['all','critical','warning','info'] as const).map(f => (
                        <button key={f} onClick={() => setFilter(f)}
                          className={`text-xs px-3 py-1 rounded-lg transition-colors capitalize ${filter===f?'bg-brand-600 text-white':'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
                        >
                          {f==='all' ? `All (${data.early_warnings.length})` : `${f} (${data.early_warnings.filter(w=>w.severity===f).length})`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    {filteredWarnings.length === 0
                      ? <p className="text-sm text-slate-400 text-center py-4">No warnings at this severity level</p>
                      : filteredWarnings.map(w => <WarningCard key={w.id} warning={w} />)
                    }
                  </div>
                </div>
              )}

              <div className="card p-0 overflow-hidden">
                <div className="px-4 py-3 border-b border-hydro-border flex items-center justify-between">
                  <h2 className="text-base font-semibold text-slate-900">Tag Health Scores</h2>
                  <div className="flex gap-1">
                    {(['all','healthy','warning','critical'] as const).map(f => (
                      <button key={f} onClick={() => setTagFilter(f)}
                        className={`text-xs px-3 py-1 rounded-lg transition-colors capitalize ${tagFilter===f?'bg-brand-600 text-white':'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
                      >{f}</button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-hydro-border text-slate-500 uppercase tracking-wider text-xs">
                        <th className="px-4 py-3 text-left">Tag</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Health Score</th>
                        <th className="px-4 py-3 text-left">Current</th>
                        <th className="px-4 py-3 text-left">Baseline</th>
                        <th className="px-4 py-3 text-left">Deviation</th>
                        <th className="px-4 py-3 text-left">Anomalies 24h</th>
                        <th className="px-4 py-3 text-left">Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTags.length === 0
                        ? <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No tags in this category</td></tr>
                        : filteredTags.sort((a,b) => a.health_score - b.health_score).map(tag => <TagRow key={tag.tag_name} tag={tag} />)
                      }
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-xs text-slate-400 text-center">
                Baseline computed from 7-day rolling window · Anomalies = readings outside ±2σ · Correlation = multi-tag causal analysis
              </div>
            </>
          )}
        </>
      )}

      {logOpen && <LogAnomalyModal anomalyTypes={anomalyTypes} onClose={() => setLogOpen(false)} />}
      {ackItem  && <AckModal anomaly={ackItem} onClose={() => setAckItem(null)} />}
    </div>
  );
}
